import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useImperativeHandle,
  useRef,
  ForwardedRef,
} from 'react';
import tw, { styled, TwStyle } from 'twin.macro';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { getCountries } from 'react-phone-number-input/input';
//@ts-expect-error - no types available
import countryNames from 'react-phone-number-input/locale/it';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { FormLabelWrapper, TextInput, ButtonV2, isValidEmail } from '@restworld/ui-ds';
import { Combobox } from '@restworld/ui-ds-base';
import { Company } from '@restworld/utility-types';
import { extractApiErrors } from '@restworld/utils-common';

import { ITALIAN_PROVINCES, getProvinceCodeFromLabel, getProvinceLabelFromCode } from './italian-provinces';
import { EmployerCompanyModalFooterContext } from './employer-company-modal-footer-context';
import useEmployerCreateCompanyMutation from '../../hooks/use-employer-create-company-mutation';
import {
  selectedCompanyAtom,
  currentCompanyActionAtom,
  isEmployerCompanyActionModalOpenAtom,
  isCompanyForeignAtom,
} from '../../employer-companies.atoms';
import useEmployerUpdateCompanyMutation from '../../hooks/use-employer-update-company-mutation';
import { isCompanyEditModalOpenAtom } from '../../../job-position-onboarding/atoms/company-edit-modal.atoms';

const CompanyDetailsForm = forwardRef<
  CompanyDetailsFormRef,
  {
    nationWrapperStyle?: TwStyle;
    hideCta?: boolean;
    onFormDirtyChange?: (isDirty: boolean) => void;
    onLoadingChange?: (isLoading: boolean) => void;
    useJPOnboardingAtoms?: boolean;
    onUpdateSuccess?: () => void;
  }
>(({ nationWrapperStyle, hideCta, onFormDirtyChange, onLoadingChange, useJPOnboardingAtoms, onUpdateSuccess }, ref) => {
  const { t } = useTranslation('payment-and-invoices');
  const footerContext = useContext(EmployerCompanyModalFooterContext);
  const currentCompanyAction = useAtomValue(currentCompanyActionAtom);
  const {
    allCountryNames,
    control,
    defaultCountry,
    errors,
    handleCancel,
    handleSwitchType,
    handleSubmit,
    isCreateMutationPending,
    isUpdateMutationPending,
    onSubmit,
    watch,
    isCompanyForeign,
  } = useCompanyDetailsForm({ ref, onFormDirtyChange, onLoadingChange, useJPOnboardingAtoms, onUpdateSuccess });

  const countryCode = watch('country_code');
  const isEditMode = currentCompanyAction === 'editCompany';

  const footerEl = useMemo(
    () => (
      <FooterButtonsWrapper>
        <ButtonV2 variant="tertiary" type="button" onClick={handleCancel}>
          {currentCompanyAction === 'editCompany' ? t('cancel') : t('back')}
        </ButtonV2>
        <ButtonV2
          type="submit"
          loading={isCreateMutationPending || isUpdateMutationPending}
          onClick={handleSubmit(onSubmit)}
        >
          {currentCompanyAction === 'createCompany' ? t('create_company') : t('confirm')}
        </ButtonV2>
      </FooterButtonsWrapper>
    ),
    [currentCompanyAction, handleCancel, handleSubmit, isCreateMutationPending, isUpdateMutationPending, onSubmit, t]
  );

  useEffect(() => {
    if (footerContext) {
      footerContext.setFooter(footerEl);
      return () => footerContext.setFooter(null);
    }
  }, [footerContext, footerEl]);

  return (
    <StyledForm>
      {isEditMode && (
        <CompanyTypeToggle>
          <CompanyTypeToggleButton active={!isCompanyForeign} onClick={() => handleSwitchType(false)} type="button">
            {t('italian_company')}
          </CompanyTypeToggleButton>
          <CompanyTypeToggleButton active={isCompanyForeign} onClick={() => handleSwitchType(true)} type="button">
            {t('foreign_company')}
          </CompanyTypeToggleButton>
        </CompanyTypeToggle>
      )}
      <InputsWrapper>
        <TopRowsWrapper $foreign={isCompanyForeign}>
          {isCompanyForeign && (
            <FormLabelWrapper label={t('nation')} isFullWidth>
              <Controller
                name="country_code"
                control={control}
                rules={{ required: t('nation_is_required') }}
                defaultValue={defaultCountry}
                render={({ field }) => (
                  <Combobox
                    dropdownList={allCountryNames}
                    inputWrapperStyle={nationWrapperStyle ? { ...tw`w-full`, ...nationWrapperStyle } : tw`w-full`}
                    inputStyle={tw`h-[50px] outline outline-neutral-70 focus:(border-2 border-neutral-100)`}
                    error={errors['country_code']?.message || ''}
                    fullwidth
                    {...field}
                  />
                )}
              />
            </FormLabelWrapper>
          )}
          <FormLabelWrapper label={t('company_name')} isFullWidth>
            <Controller
              name="name"
              control={control}
              rules={{ required: t('company_name_is_required') }}
              render={({ field }) => (
                <TextInput
                  type="text"
                  id="company_name"
                  bordercolor="neutral-100"
                  isError={!!errors['name']}
                  supportText={errors['name']?.message || ''}
                  {...field}
                />
              )}
            />
          </FormLabelWrapper>
          <FormLabelWrapper label={isCompanyForeign ? t('vat_number') : t('vat')} isFullWidth>
            <Controller
              name="vat_number"
              control={control}
              rules={{ required: isCompanyForeign ? t('vat_number_is_required') : t('vat_is_required') }}
              render={({ field }) => (
                <TextInput
                  type="text"
                  id="vat"
                  bordercolor="neutral-100"
                  isError={!!errors['vat_number']}
                  supportText={
                    isCompanyForeign
                      ? errors['vat_number']?.message || t('foreign_vat_helper')
                      : errors['vat_number']?.message || ''
                  }
                  {...field}
                />
              )}
            />
          </FormLabelWrapper>
          {!isCompanyForeign && (
            <FormLabelWrapper label={t('sdi')} isFullWidth>
              <Controller
                name="sdi_code"
                control={control}
                rules={{ required: !isCompanyForeign && t('sdi_is_required') }}
                render={({ field }) => (
                  <TextInput
                    type="text"
                    id="sdi"
                    bordercolor="neutral-100"
                    isError={!!errors['sdi_code']}
                    supportText={
                      !countryCode || (countryCode !== 'Italia' && countryCode !== 'IT')
                        ? 'Il codice univoco è obbligatorio solo per le aziende italiane'
                        : errors['sdi_code']?.message || ''
                    }
                    {...field}
                    disabled={!countryCode || (countryCode !== 'Italia' && countryCode !== 'IT')}
                  />
                )}
              />
            </FormLabelWrapper>
          )}
          <FormLabelWrapper label={t('billing_email')} isFullWidth>
            <Controller
              name="billing_email_address"
              control={control}
              rules={{
                required: t('billing_email_is_required'),
                validate: (value) => isValidEmail(value) || t('insert_a_valid_email'),
              }}
              render={({ field }) => (
                <TextInput
                  type="email"
                  id="billing_email"
                  bordercolor="neutral-100"
                  isError={!!errors['billing_email_address']}
                  supportText={errors['billing_email_address']?.message || ''}
                  {...field}
                />
              )}
            />
          </FormLabelWrapper>
        </TopRowsWrapper>
        {!isCompanyForeign && (
          <MiddleRowWrapper>
            <FormLabelWrapper label={t('nation')} isFullWidth>
              <Controller
                name="country_code"
                control={control}
                rules={{ required: t('nation_is_required') }}
                defaultValue={defaultCountry}
                render={({ field }) => (
                  <Combobox
                    dropdownList={allCountryNames}
                    inputWrapperStyle={nationWrapperStyle ? { ...tw`w-full`, ...nationWrapperStyle } : tw`w-full`}
                    inputStyle={tw`h-[50px] outline outline-neutral-70 focus:(border-2 border-neutral-100)`}
                    error={errors['country_code']?.message || ''}
                    fullwidth
                    {...field}
                  />
                )}
              />
            </FormLabelWrapper>
            <FormLabelWrapper label={t('cap')} isFullWidth>
              <Controller
                name="zip_code"
                control={control}
                rules={{ required: t('cap_is_required') }}
                render={({ field }) => (
                  <TextInput
                    type="text"
                    id="cap"
                    bordercolor="neutral-100"
                    isError={!!errors['zip_code']}
                    supportText={errors['zip_code']?.message || ''}
                    {...field}
                  />
                )}
              />
            </FormLabelWrapper>
            <FormLabelWrapper label={t('province_code')} isFullWidth>
              <Controller
                name="province_code"
                control={control}
                rules={{ required: t('province_code_is_required') }}
                defaultValue=""
                render={({ field }) => (
                  <Combobox
                    dropdownList={ITALIAN_PROVINCES}
                    inputWrapperStyle={tw`w-full`}
                    inputStyle={tw`h-[50px] outline outline-neutral-70 focus:(border-2 border-neutral-100)`}
                    error={errors['province_code']?.message || ''}
                    fullwidth
                    {...field}
                  />
                )}
              />
            </FormLabelWrapper>
          </MiddleRowWrapper>
        )}
        <BottomRowWrapper $foreign={isCompanyForeign}>
          <FormLabelWrapper label={t('city')} isFullWidth>
            <Controller
              name="city"
              control={control}
              rules={{ required: t('city_is_required') }}
              render={({ field }) => (
                <TextInput
                  type="text"
                  id="city"
                  bordercolor="neutral-100"
                  isError={!!errors['city']}
                  supportText={errors['city']?.message || ''}
                  {...field}
                />
              )}
            />
          </FormLabelWrapper>
          <FormLabelWrapper label={t('address')} isFullWidth>
            <Controller
              name="street_name"
              control={control}
              rules={{ required: t('address_is_required') }}
              render={({ field }) => (
                <TextInput
                  type="text"
                  id="address"
                  bordercolor="neutral-100"
                  isError={!!errors['street_name']}
                  supportText={errors['street_name']?.message || ''}
                  {...field}
                />
              )}
            />
          </FormLabelWrapper>
          {!isCompanyForeign && (
            <FormLabelWrapper label={t('street_number')} isFullWidth>
              <Controller
                name="house_number"
                control={control}
                rules={{ required: t('street_number_is_required') }}
                render={({ field }) => (
                  <TextInput
                    type="text"
                    id="street_number"
                    bordercolor="neutral-100"
                    isError={!!errors['house_number']}
                    supportText={errors['house_number']?.message || ''}
                    {...field}
                  />
                )}
              />
            </FormLabelWrapper>
          )}
        </BottomRowWrapper>
      </InputsWrapper>
      {!hideCta && !footerContext && (
        <ActionButtonsWrapper>
          <ButtonV2 variant="tertiary" type="button" onClick={handleCancel}>
            {currentCompanyAction === 'editCompany' ? t('cancel') : t('back')}
          </ButtonV2>
          <ButtonV2
            type="submit"
            loading={isCreateMutationPending || isUpdateMutationPending}
            onClick={handleSubmit(onSubmit)}
          >
            {currentCompanyAction === 'createCompany' ? t('create_company') : t('confirm')}
          </ButtonV2>
        </ActionButtonsWrapper>
      )}
    </StyledForm>
  );
});
CompanyDetailsForm.displayName = 'CompanyDetailsForm';
export default CompanyDetailsForm;

export type CompanyDetailsFormRef = {
  submit: () => Promise<void>;
  isLoading: boolean;
};

function formatFieldName(fieldName: string) {
  // Replace underscores with spaces and capitalize the first letter of the string
  return fieldName.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function getCountryCodeFromName(countryName: string) {
  let countryCode: string;

  for (const [code, name] of Object.entries(countryNames)) {
    if (typeof name === 'string' && name.toLowerCase() === countryName.toLowerCase()) {
      countryCode = code;
      break;
    }
  }

  return countryCode;
}

function useCompanyDetailsForm({
  ref,
  onFormDirtyChange,
  onLoadingChange,
  useJPOnboardingAtoms,
  onUpdateSuccess,
}: {
  ref: ForwardedRef<CompanyDetailsFormRef>;
  onFormDirtyChange?: (isDirty: boolean) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  useJPOnboardingAtoms?: boolean;
  onUpdateSuccess?: () => void;
}) {
  const selectedCompany = useAtomValue(selectedCompanyAtom);
  const [currentCompanyAction, setCurrentCompanyAction] = useAtom(currentCompanyActionAtom);
  const setIsEmployerCompanyActionModalOpen = useSetAtom(isEmployerCompanyActionModalOpenAtom);
  const setIsCompanyEditModalOpen = useSetAtom(isCompanyEditModalOpenAtom);
  const [isCompanyForeign, setIsCompanyForeign] = useAtom(isCompanyForeignAtom);

  const countryCodes = useMemo(() => getCountries(), []);
  const allCountryNames = useMemo(() => countryCodes.map((code) => countryNames[code]), [countryCodes]);

  // Auto-detect foreign company in edit mode
  useEffect(() => {
    if (currentCompanyAction === 'editCompany' && selectedCompany?.country_code) {
      setIsCompanyForeign(selectedCompany.country_code !== 'IT');
    }
  }, [currentCompanyAction, selectedCompany?.country_code, setIsCompanyForeign]);

  const {
    control,
    formState: { errors, isDirty },
    setValue,
    handleSubmit,
    setError,
    watch,
  } = useForm<Partial<Company>>();

  const countryCode = watch('country_code');

  // Notify parent component when form dirty state changes
  useEffect(() => {
    if (onFormDirtyChange) {
      onFormDirtyChange(isDirty);
    }
  }, [isDirty, onFormDirtyChange]);

  const {
    mutateAsync: createCompanyMutation,
    isPending: isCreateMutationPending,
    isError: isCreateError,
    error: createError,
  } = useEmployerCreateCompanyMutation(useJPOnboardingAtoms ? onUpdateSuccess : undefined);
  const {
    mutateAsync: updateCompanyMutation,
    isPending: isUpdateMutationPending,
    isError: isUpdateError,
    error: updateError,
  } = useEmployerUpdateCompanyMutation(useJPOnboardingAtoms ? onUpdateSuccess : undefined);

  // Notify parent component when loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isCreateMutationPending || isUpdateMutationPending);
    }
  }, [isCreateMutationPending, isUpdateMutationPending, onLoadingChange]);

  const defaultCountry: string = useMemo(
    () => (selectedCompany?.country_code ? countryNames[selectedCompany?.country_code] : ''),
    [selectedCompany?.country_code]
  );

  const onSubmit = useCallback(
    async ({
      name,
      vat_number,
      street_name,
      country_code,
      province_code,
      house_number,
      zip_code,
      billing_email_address,
      sdi_code,
      city,
    }: Partial<Company>) => {
      const resolvedCountryCode = getCountryCodeFromName(country_code);
      const isForeign = isCompanyForeign;

      const newCompany: Partial<Company> = {
        ...selectedCompany,
        name,
        vat_number,
        billing_email_address,
        country_code: resolvedCountryCode,
        // Foreign companies: use FIC conventional values
        province_code: isForeign ? 'EE' : getProvinceCodeFromLabel(province_code),
        zip_code: isForeign ? '00000' : zip_code,
        sdi_code: isForeign ? 'XXXXXXX' : sdi_code,
        fiscal_code: isForeign ? '' : selectedCompany?.fiscal_code,
        street_name,
        house_number: isForeign ? '' : house_number,
        city,
        registered_office: isForeign
          ? `${street_name}, ${city}`
          : `${street_name} ${house_number}, ${city} (${getProvinceCodeFromLabel(province_code)}) - ${zip_code}`,
      };

      if (currentCompanyAction === 'createCompany' || (useJPOnboardingAtoms && !selectedCompany?.id))
        await createCompanyMutation(newCompany);
      else if (currentCompanyAction === 'editCompany' || (useJPOnboardingAtoms && selectedCompany?.id)) {
        await updateCompanyMutation(newCompany);
      }
    },
    [
      createCompanyMutation,
      currentCompanyAction,
      isCompanyForeign,
      selectedCompany,
      updateCompanyMutation,
      useJPOnboardingAtoms,
    ]
  );

  const handleSwitchType = useCallback(
    (switchToForeign: boolean) => {
      setIsCompanyForeign(switchToForeign);
      if (switchToForeign) {
        setValue('country_code', '');
      } else {
        setValue('country_code', 'Italia');
      }
    },
    [setIsCompanyForeign, setValue]
  );

  const handleCancel = useCallback(() => {
    if (useJPOnboardingAtoms) {
      setIsCompanyEditModalOpen(false);
    } else if (currentCompanyAction === 'createCompany') {
      setCurrentCompanyAction(isCompanyForeign ? 'chooseCompanyType' : 'searchCompany');
    } else {
      setIsEmployerCompanyActionModalOpen(false);
    }
  }, [
    currentCompanyAction,
    isCompanyForeign,
    setCurrentCompanyAction,
    setIsEmployerCompanyActionModalOpen,
    setIsCompanyEditModalOpen,
    useJPOnboardingAtoms,
  ]);

  useEffect(() => {
    // if (!selectedCompany?.vat_number) return;

    const {
      name,
      vat_number,
      street_name,
      province_code,
      house_number,
      zip_code,
      billing_email_address,
      sdi_code,
      city,
    } = selectedCompany;

    setValue('name', name);
    setValue('vat_number', vat_number);
    setValue('street_name', street_name);
    setValue('country_code', defaultCountry);
    setValue('province_code', getProvinceLabelFromCode(province_code));
    setValue('house_number', house_number);
    setValue('zip_code', zip_code);
    setValue('billing_email_address', billing_email_address || '');
    setValue('city', city);

    // Set initial sdi_code based on selected company
    setValue('sdi_code', selectedCompany.country_code === 'IT' ? sdi_code || '' : 'XXXXXXX');
  }, [setValue, selectedCompany, defaultCountry]);

  // Handle sdi_code when country changes (skip initial mount — handled by initial values effect)
  const isInitialCountrySet = useRef(true);
  useEffect(() => {
    if (!countryCode) return;

    if (isInitialCountrySet.current) {
      isInitialCountrySet.current = false;
      return;
    }

    const isItaly = countryCode === 'Italia' || countryCode === 'IT';
    if (isItaly) {
      // When switching TO Italy, clear the field to allow user input
      setValue('sdi_code', '');
    } else {
      // When NOT Italy, set to default value for non-Italian companies
      setValue('sdi_code', 'XXXXXXX');
    }
  }, [countryCode, setValue]);

  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(onSubmit)(),
    isLoading: isCreateMutationPending || isUpdateMutationPending,
  }));

  useEffect(() => {
    const error = isCreateError ? createError : isUpdateError ? updateError : null;

    if (error) {
      const currentErrors = extractApiErrors<Partial<Company>>(error);
      currentErrors.forEach(({ name, type, message }) => {
        setError(name, { type, message: `${formatFieldName(name)} ${message}` });
      });
    }
  }, [createError, isCreateError, isUpdateError, setError, updateError]);

  return {
    handleSubmit,
    onSubmit,
    errors,
    allCountryNames,
    defaultCountry,
    control,
    handleCancel,
    handleSwitchType,
    isCreateMutationPending,
    isUpdateMutationPending,
    watch,
    isCompanyForeign,
  };
}

const StyledForm = styled.form`
  ${tw`flex flex-col justify-between h-full px-4 md:px-0`}
`;
const InputsWrapper = styled.div`
  ${tw``}
`;
const TopRowsWrapper = styled.div<{ $foreign?: boolean }>(({ $foreign }) => [
  tw`grid grid-cols-1 gap-5`,
  $foreign ? tw`md:grid-cols-3` : tw`md:grid-cols-2`,
]);
const MiddleRowWrapper = styled.div`
  ${tw`grid grid-cols-1 gap-5 mt-12 md:grid-cols-3`}
`;
const BottomRowWrapper = styled.div<{ $foreign?: boolean }>(({ $foreign }) => [
  tw`grid grid-cols-1 gap-5`,
  $foreign ? tw`mt-12 md:grid-cols-2` : tw`mt-5 md:grid-cols-3`,
]);
const ActionButtonsWrapper = styled.div`
  ${tw`flex justify-between items-center mt-8 pb-4 md:pb-0`}
`;
const FooterButtonsWrapper = styled.div`
  ${tw`flex flex-row gap-5 w-full justify-between`}
  & > button {
    ${tw`flex-1 md:flex-none`}
  }
`;
const CompanyTypeToggle = styled.div`
  ${tw`flex gap-1 p-1 rounded-xl bg-neutral-40 mb-6`}
`;
const CompanyTypeToggleButton = styled.button<{ active: boolean }>`
  ${tw`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all`}
  ${({ active }) =>
    active ? tw`bg-neutral-10 text-neutral-700 shadow-sm` : tw`text-neutral-500 hover:text-neutral-700`}
`;
